import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import {
  allWords,
  isOneAway,
  matchingGroupIndex,
  normalizePuzzle,
  shuffle,
  TIER_RANK,
  type GroupThinkLevel,
  type GroupThinkPuzzle,
} from './types';
import './groupThink.css';

export default function GroupThinkGame() {
  const [data, setData] = useState<{
    puzzle: GroupThinkPuzzle;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<GroupThinkLevel>('groupThink')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Groupthink yet.');
          return;
        }
        const puzzle = normalizePuzzle(res.data);
        if (!puzzle) {
          setLoadError("Today's Groupthink puzzle is malformed.");
          return;
        }
        setData({ puzzle, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="groupThink" />;
  if (!data) {
    return (
      <GameShell gameId="groupThink">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('groupThink', data.dateKey)) {
    return (
      <FinishedDaily gameId="groupThink" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <GroupThinkPlay
      key={data.dateKey}
      puzzle={data.puzzle}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

function GroupThinkPlay({
  puzzle,
  dateKey,
  isToday,
}: {
  puzzle: GroupThinkPuzzle;
  dateKey: string;
  isToday: boolean;
}) {
  // Shuffled play order — every visit scrambles the 16 tiles so positions
  // can't be memorised across replays.
  const { restored, save } = useDailySnapshot<{
    tileOrder: string[];
    solvedOrder: number[];
    mistakes: number;
    hintUsed: boolean;
  }>('groupThink', dateKey);

  // The shuffle is part of the round: restoring it keeps tiles where the player
  // left them rather than re-scrambling the board on every return.
  const [tileOrder, setTileOrder] = useState<string[]>(
    () => restored?.tileOrder ?? shuffle(allWords(puzzle)),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [solvedOrder, setSolvedOrder] = useState<number[]>(
    () => restored?.solvedOrder ?? [],
  );
  const [mistakes, setMistakes] = useState(restored?.mistakes ?? 0);
  const [outcome, setOutcome] = useState<'won' | 'lost' | null>(null);
  const [shake, setShake] = useState(false);
  const [oneAway, setOneAway] = useState(false);
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(save, { tileOrder, solvedOrder, mistakes, hintUsed }, outcome === null);

  // Timers are cleared on unmount so a late auto-solve can't fire into an
  // unmounted tree.
  const timers = useRef<number[]>([]);
  const after = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);
  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
    },
    [],
  );

  const settledRef = useRef(false);
  useEffect(() => {
    if (!outcome || settledRef.current) return;
    settledRef.current = true;
    if (outcome === 'won') {
      recordOutcome('groupThink', dateKey, 'won', {
        subtitle:
          mistakes === 0
            ? 'Perfect — no mistakes.'
            : `Solved with ${mistakes} mistake${mistakes === 1 ? '' : 's'}.`,
      });
    } else {
      recordOutcome('groupThink', dateKey, 'lost', {
        subtitle: 'Ran out of mistakes.',
        answer: puzzle.groups.map((g) => g.category).join(' · '),
      });
    }
  }, [outcome, dateKey, mistakes, puzzle]);

  const solvedWords = useMemo(() => {
    const s = new Set<string>();
    for (const idx of solvedOrder) for (const w of puzzle.groups[idx].words) s.add(w);
    return s;
  }, [solvedOrder, puzzle]);

  const unsolvedTiles = useMemo(
    () => tileOrder.filter((w) => !solvedWords.has(w)),
    [tileOrder, solvedWords],
  );

  const isGameOver = outcome !== null;

  /**
   * Pin a freshly-solved group to the top and reorder the remaining tiles.
   * Mirrors `lockGroup(at:)` — including the auto-solve of the final group
   * once three are down, which saves a redundant tap.
   */
  const lockGroup = useCallback(
    (groupIdx: number) => {
      setSolvedOrder((prev) => {
        if (prev.includes(groupIdx)) return prev;
        const next = [...prev, groupIdx];

        const pinned: string[] = [];
        for (const idx of next) pinned.push(...puzzle.groups[idx].words);
        const pinnedSet = new Set(pinned);
        setTileOrder((order) => [...pinned, ...order.filter((w) => !pinnedSet.has(w))]);

        if (next.length === 3) {
          const last = puzzle.groups.findIndex((_, i) => !next.includes(i));
          if (last >= 0) after(600, () => lockGroup(last));
        } else if (next.length === 4) {
          setOutcome('won');
        }
        return next;
      });
      setSelected(new Set());
      setOneAway(false);
    },
    [puzzle, after],
  );

  const toggle = useCallback(
    (word: string) => {
      if (isGameOver) return;
      setOneAway(false);
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(word)) next.delete(word);
        else if (next.size < 4) next.add(word);
        return next;
      });
    },
    [isGameOver],
  );

  const deselectAll = useCallback(() => setSelected(new Set()), []);

  /** Shuffle only the unsolved tiles; solved rows stay pinned to the top. */
  const shuffleUnsolved = useCallback(() => {
    setTileOrder((order) => {
      const pinned = order.filter((w) => solvedWords.has(w));
      const rest = shuffle(order.filter((w) => !solvedWords.has(w)));
      return [...pinned, ...rest];
    });
  }, [solvedWords]);

  const submit = useCallback(() => {
    if (selected.size !== 4 || isGameOver) return;

    const groupIdx = matchingGroupIndex(puzzle, selected);
    if (groupIdx >= 0) {
      lockGroup(groupIdx);
      return;
    }

    // Wrong — flag "one away" when 3 of 4 share a group.
    setOneAway(isOneAway(puzzle, selected));
    setShake(true);
    after(500, () => setShake(false));

    setMistakes((m) => {
      const next = m + 1;
      if (next >= puzzle.maxMistakes) after(500, () => setOutcome('lost'));
      return next;
    });
  }, [selected, isGameOver, puzzle, lockGroup, after]);

  /**
   * A hint reveals the easiest (lowest-tier) unsolved group, exactly as if the
   * player had picked those four tiles. Only meaningful while at least two
   * groups remain — with one left the board resolves itself.
   */
  const canUseHint =
    !isGameOver && !hintUsed && puzzle.groups.length - solvedOrder.length >= 2;

  const useHint = useCallback(() => {
    if (!canUseHint) return;
    const unsolved = puzzle.groups
      .map((_, i) => i)
      .filter((i) => !solvedOrder.includes(i));
    if (!unsolved.length) return;
    const idx = unsolved.reduce((best, i) =>
      TIER_RANK[puzzle.groups[i].tier] < TIER_RANK[puzzle.groups[best].tier] ? i : best,
    );
    setHintUsed(true);
    setSelected(new Set());
    lockGroup(idx);
  }, [canUseHint, puzzle, solvedOrder, lockGroup]);

  const purpleFirst =
    outcome === 'won' &&
    solvedOrder.length > 0 &&
    puzzle.groups[solvedOrder[0]].tier === 'purple';

  const rows: string[][] = [];
  for (let i = 0; i < unsolvedTiles.length; i += 4) rows.push(unsolvedTiles.slice(i, i + 4));

  return (
    <GameShell
      gameId="groupThink"
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${solvedOrder.length}/4 groups`}
    >
      <div className="gt-board">
        {puzzle.theme && <div className="gt-theme">{puzzle.theme}</div>}

        <div className={'gt-grid' + (shake ? ' is-shake' : '')}>
          {solvedOrder.map((groupIdx) => {
            const group = puzzle.groups[groupIdx];
            return (
              <div key={group.category} className={`gt-solved tier-${group.tier}`}>
                <div className="gt-solved-category">{group.category.toUpperCase()}</div>
                <div className="gt-solved-words">{group.words.join(' · ')}</div>
              </div>
            );
          })}

          {rows.map((row, r) => (
            <div key={r} className="gt-row">
              {row.map((word) => (
                <button
                  key={word}
                  type="button"
                  className={
                    'gt-tile' +
                    (selected.has(word) ? ' is-selected' : '') +
                    ` len-${word.length <= 5 ? 's' : word.length <= 7 ? 'm' : word.length <= 9 ? 'l' : 'xl'}`
                  }
                  onClick={() => toggle(word)}
                  disabled={isGameOver}
                >
                  {word}
                </button>
              ))}
            </div>
          ))}
        </div>

        {oneAway && <div className="gt-oneaway">ONE AWAY…</div>}

        <div className="gt-mistakes">
          <span className="gt-mistakes-label">MISTAKES</span>
          <div className="gt-mistakes-dots">
            {Array.from({ length: puzzle.maxMistakes }).map((_, i) => (
              <span
                key={i}
                className={'gt-dot' + (i < mistakes ? ' is-spent' : '')}
                aria-hidden
              />
            ))}
          </div>
        </div>

        <div className="gt-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={shuffleUnsolved}
            disabled={isGameOver}
          >
            Shuffle
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={deselectAll}
            disabled={isGameOver || selected.size === 0}
          >
            Deselect
          </button>
          <button
            type="button"
            className="btn btn-accent"
            onClick={useHint}
            disabled={!canUseHint}
          >
            Hint
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={selected.size !== 4 || isGameOver}
          >
            Submit
          </button>
        </div>

        {outcome === 'won' && (
          <DailyDoneOverlay
            dateKey={dateKey}
            isToday={isToday}
            subtitle={
              (mistakes === 0
                ? 'Perfect — no mistakes.'
                : `Solved with ${mistakes} mistake${mistakes === 1 ? '' : 's'}.`) +
              (purpleFirst ? ' Purple-first bonus!' : '')
            }
          />
        )}

        {outcome === 'lost' && (
          <div className="gt-overlay">
            <div className="gt-overlay-card">
              <div className="gt-overlay-emoji">😬</div>
              <div className="gt-overlay-title">Out of mistakes</div>
              <div className="gt-overlay-sub">The groups were</div>
              <div className="gt-overlay-groups">
                {puzzle.groups.map((g) => (
                  <div key={g.category} className={`gt-solved tier-${g.tier}`}>
                    <div className="gt-solved-category">{g.category.toUpperCase()}</div>
                    <div className="gt-solved-words">{g.words.join(' · ')}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </GameShell>
  );
}
